import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = { renderId: Number, auditData: String }
  static targets = [
    "auditBtn", "auditPanel", "promptDisplay",
    "responseDisplay", "statusBar", "spinnerIcon", "statusText"
  ]

  connect() {
    if (!this.renderIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: "TelemetryDashboardChannel", render_id: this.renderIdValue },
      { received: (data) => this.handleBroadcast(data) }
    )

    if (this.hasAuditDataValue && this.auditDataValue) {
      this.loadPersistedAudit()
    }
  }

  loadPersistedAudit() {
    try {
      const data = JSON.parse(this.auditDataValue)
      this.auditPanelTarget.style.display = ""
      this.statusBarTarget.style.display = ""
      this.spinnerIconTarget.style.display = "none"
      this.statusTextTarget.textContent = "Audit complete"
      if (data.prompt) this.promptDisplayTarget.textContent = data.prompt
      this.renderAuditResponse(data.response, data.input_tokens, data.output_tokens)
    } catch (_) {}
  }

  disconnect() {
    this.subscription?.unsubscribe()
  }

  handleBroadcast(data) {
    if (data.action !== "audit_update") return

    switch (data.status) {
      case "uploading":
      case "analyzing":
        this.showStatus(data.message)
        break
      case "completed":
        this.spinnerIconTarget.style.display = "none"
        this.statusTextTarget.textContent = data.message
        this.auditPanelTarget.style.display = ""
        if (data.prompt) this.promptDisplayTarget.textContent = data.prompt
        this.renderAuditResponse(data.response, data.input_tokens, data.output_tokens)
        this.auditBtnTarget.disabled = false
        break
      case "failed":
        this.spinnerIconTarget.style.display = "none"
        this.statusBarTarget.style.display = ""
        this.statusTextTarget.textContent = `Error: ${data.error}`
        this.auditBtnTarget.disabled = false
        break
    }
  }

  async audit() {
    this.auditBtnTarget.disabled = true
    this.statusBarTarget.style.display = ""
    this.spinnerIconTarget.style.display = ""
    this.statusTextTarget.textContent = "Queuing audit job..."
    this.auditPanelTarget.style.display = "none"
    this.promptDisplayTarget.textContent = ""
    this.responseDisplayTarget.textContent = ""

    try {
      const resp = await fetch(`/telemetry/${this.renderIdValue}/audit`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!resp.ok) {
        const data = await resp.json()
        this.statusTextTarget.textContent = `Error: ${data.error}`
        this.spinnerIconTarget.style.display = "none"
        this.auditBtnTarget.disabled = false
      }
    } catch (e) {
      this.statusTextTarget.textContent = `Error: ${e.message}`
      this.spinnerIconTarget.style.display = "none"
      this.auditBtnTarget.disabled = false
    }
  }

  showStatus(message) {
    this.statusBarTarget.style.display = ""
    this.spinnerIconTarget.style.display = ""
    this.statusTextTarget.textContent = message
  }

  renderAuditResponse(response, inputTokens, outputTokens) {
    const container = this.responseDisplayTarget
    container.textContent = ""

    if (!response || response.raw_response) {
      container.textContent = response?.raw_response || "No structured response"
      return
    }

    const header = document.createElement("div")
    header.className = "telemetry__audit-header"

    const scoreEl = document.createElement("div")
    scoreEl.className = "telemetry__audit-score"
    scoreEl.textContent = `Overall: ${response.overall_score}/10`
    header.appendChild(scoreEl)

    if (inputTokens || outputTokens) {
      const tokensEl = document.createElement("div")
      tokensEl.className = "telemetry__audit-tokens"
      tokensEl.textContent = `Tokens: ${inputTokens || 0} in / ${outputTokens || 0} out`
      header.appendChild(tokensEl)
    }

    container.appendChild(header)

    if (response.summary) {
      const summaryEl = document.createElement("p")
      summaryEl.className = "telemetry__audit-summary"
      summaryEl.textContent = response.summary
      container.appendChild(summaryEl)
    }

    if (response.categories) {
      const grid = document.createElement("div")
      grid.className = "telemetry__audit-grid"

      for (const [key, val] of Object.entries(response.categories)) {
        const card = document.createElement("div")
        card.className = "telemetry__audit-category"

        const label = document.createElement("div")
        label.className = "telemetry__audit-category-label"
        label.textContent = key.replace(/_/g, " ")

        const score = document.createElement("div")
        score.className = `telemetry__audit-category-score telemetry__audit-category-score--${val.score >= 7 ? "good" : val.score >= 5 ? "mid" : "low"}`
        score.textContent = `${val.score}/10`

        const notes = document.createElement("div")
        notes.className = "telemetry__audit-category-notes"
        notes.textContent = val.notes || ""

        card.appendChild(label)
        card.appendChild(score)
        card.appendChild(notes)
        grid.appendChild(card)
      }
      container.appendChild(grid)
    }

    if (response.suggestions?.length) {
      const sugTitle = document.createElement("h4")
      sugTitle.textContent = "Suggestions"
      container.appendChild(sugTitle)

      const list = document.createElement("ul")
      list.className = "telemetry__audit-suggestions"

      for (const sug of response.suggestions) {
        const li = document.createElement("li")
        li.className = `telemetry__audit-suggestion telemetry__audit-suggestion--${sug.priority}`

        const badge = document.createElement("span")
        badge.className = "telemetry__audit-priority"
        badge.textContent = sug.priority

        if (sug.timestamp) {
          const ts = document.createElement("span")
          ts.className = "telemetry__audit-timestamp"
          ts.textContent = sug.timestamp
          li.appendChild(ts)
        }

        const desc = document.createElement("span")
        desc.textContent = sug.description

        li.appendChild(badge)
        li.appendChild(desc)
        list.appendChild(li)
      }
      container.appendChild(list)
    }
  }
}
